import {
  AI_AUTOPILOT_USER_ID,
  CHATBOT_USER_ID,
} from "@redotech/ai-autopilot-constants/constant";
import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import {
  MongoUserId,
  type MongoUserId as MongoUserIdType,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { GetUser as User } from "@redotech/redo-model/user";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import ChatBotIcon from "@redotech/redo-web/icon-old/chat-bot.svg";
import { ObjectId } from "bson";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useUpdateConversationAssignee } from "./use-conversation-mutations";

const searcher = new Fuse<User>([], {
  keys: ["name", "email"],
  threshold: 0.3,
});

export function useAssigneeSelector({
  conversationId,
  mongoAssigneeId,
}: {
  conversationId: ConversationId;
  mongoAssigneeId: MongoUserIdType | null;
}): {
  searchText: string;
  setSearchText: (text: string) => void;
  canEditAssignee: boolean;
  assigneeListItems: RedoListItem<User | null>[];
  selectedItem: RedoListItem<User | null> | undefined;
  handleSelectAssignee: (user: User | null) => void;
} {
  const team = useRequiredContext(TeamContext);
  const updateAssignee = useUpdateConversationAssignee();
  const [searchText, setSearchText] = useState("");
  const canEditAssignee = useHasPermission(SupportPermissions.EDIT_ASSIGNEE);
  const allUsers = useMemo(
    () => team.users.map((user) => user.user as User),
    [team.users],
  );
  const filteredUsers = useSearch(searcher, allUsers, searchText);
  const assigneeListItems: RedoListItem<User | null>[] = useMemo(() => {
    const userItems: RedoListItem<User>[] = filteredUsers.map((user) => {
      const name =
        user._id === CHATBOT_USER_ID
          ? "Chatbot"
          : user._id === AI_AUTOPILOT_USER_ID
            ? "AI Autopilot"
            : user.name;
      return {
        type: "text",
        id: MongoUserId.fromObjectId(new ObjectId(user._id)),
        text: name,
        value: user,
        leadingItem:
          user._id === CHATBOT_USER_ID
            ? { type: "icon", Icon: ChatBotIcon }
            : user._id === AI_AUTOPILOT_USER_ID
              ? { type: "icon", Icon: AiStarIcon }
              : {
                  type: "avatar",
                  alt: "User profile picture",
                  imageUrl: getUserAvatarUrl({ email: user.email }),
                  name,
                },
      };
    });

    const unassigned: RedoListItem<null> = {
      type: "text",
      id: "unassigned",
      text: "Unassigned",
      value: null,
    };

    return [unassigned, ...userItems];
  }, [filteredUsers]);

  const currentAssignee = useMemo(() => {
    if (!mongoAssigneeId) {
      return null;
    }
    const hexId = MongoUserId.toObjectId(mongoAssigneeId).toHexString();
    return allUsers.find((user) => user._id === hexId) ?? null;
  }, [mongoAssigneeId, allUsers]);

  const selectedItem: RedoListItem<User | null> | undefined = useMemo(() => {
    if (!currentAssignee) {
      return {
        type: "text",
        id: "unassigned",
        text: "Unassigned",
        value: null,
      };
    }
    const name =
      currentAssignee._id === CHATBOT_USER_ID
        ? "Chatbot"
        : currentAssignee._id === AI_AUTOPILOT_USER_ID
          ? "AI Autopilot"
          : currentAssignee.name;
    return {
      type: "text",
      id: mongoAssigneeId!,
      text: name,
      value: currentAssignee,
      leadingItem:
        currentAssignee._id === CHATBOT_USER_ID
          ? { type: "icon", Icon: ChatBotIcon }
          : currentAssignee._id === AI_AUTOPILOT_USER_ID
            ? { type: "icon", Icon: AiStarIcon }
            : {
                type: "avatar",
                alt: "User profile picture",
                imageUrl: getUserAvatarUrl({ email: currentAssignee.email }),
                name,
              },
    };
  }, [currentAssignee, mongoAssigneeId]);

  const handleSelectAssignee = useHandler((user: User | null) => {
    const newAssigneeId = user ? MongoUserId.make(user._id) : null;
    updateAssignee(conversationId, newAssigneeId);
  });

  return {
    searchText,
    setSearchText,
    canEditAssignee,
    assigneeListItems,
    selectedItem,
    handleSelectAssignee,
  };
}
